import { z } from 'zod'

import { appointmentSchema } from '../../entities/appointment'
import { clinicIdSchema } from '../../entities/clinic'
import { consultationSchema } from '../../entities/consultation'
import { orderUrgencySchema } from '../../entities/order'
import { batchAllocationItemSchema } from '../../entities/stock'
import {
  refillIntervalSchema,
  type TreatmentType,
  treatmentPrescriptionContextSchema,
} from '../../entities/treatment'
import { treatmentReminderTargetSchema } from '../../entities/treatment-reminder'
import { ValidationError } from '../../lib/errors'
import { writtenPrescriptionInputSchema } from './documents'
import { treatmentWithRelationsSchema } from './relations'

/** A treatment as the treatments routes return it: its relations, prescription state and consultation. */
export const treatmentWithContextSchema = treatmentWithRelationsSchema.extend({
  ...treatmentPrescriptionContextSchema.shape,
  consultation: consultationSchema.extend({ appointment: appointmentSchema.nullable() }).nullable(),
})

export type TreatmentWithContext = z.output<typeof treatmentWithContextSchema>

// ---------------------------------------------------------------------------
// POST /api/treatments — request body
//
// Each item is a discriminated union on `type` which describes the
// *dispensing intent* — i.e. the shape of data the client is sending
// and how the backend should process it. The backend derives the internal
// `treatmentType` (vaccination/prescription/procedure) from the product.
//
//   internal                            → pending order in dispensing queue
//   internal_administered               → dispensed immediately, stock deducted
//   external_prescription               → written script, no stock impact
//   external_prescription_with_task     → written script + clinic task
// ---------------------------------------------------------------------------

const sharedFields = {
  quantity: z.number().positive().multipleOf(0.01),
  discountBps: z.number().int().min(0).max(10000),
  clinicalNotes: z.string().optional(),
  internalNotes: z.string().optional(),
  repeatsAllowed: z.number().int().min(0).optional(),
  refillInterval: z.null().optional(),
  validUntil: z.iso.datetime().optional(),
}

const internalFields = {
  ...sharedFields,
  productId: z.uuid(),
  clinicId: clinicIdSchema,
  /** Physical vaccine tag number — only meaningful for vaccine products. */
  tagNumber: z.string().optional(),
  /** Free-text injection site — only meaningful for vaccine products. */
  injectionSite: z.string().optional(),
}

/** Take-home item — creates a pending order in the dispensing queue. */
const createInternalItemSchema = z
  .object({
    type: z.literal('internal'),
    ...internalFields,
    instructions: z.string().optional(),
    urgency: orderUrgencySchema.optional(),
    refillInterval: refillIntervalSchema.nullish(),
  })
  .superRefine((item, ctx) => {
    if ((item.repeatsAllowed ?? 0) > 0 && !item.refillInterval) {
      ctx.addIssue({
        code: 'custom',
        path: ['refillInterval'],
        message: 'Refill interval is required for internal prescriptions with refills',
      })
    }
    if ((item.repeatsAllowed ?? 0) === 0 && item.refillInterval) {
      ctx.addIssue({
        code: 'custom',
        path: ['refillInterval'],
        message: 'Refill interval requires an authorised refill allowance',
      })
    }
  })

/** Administered in consult — immediate stock deduction via explicit allocations. */
const createInternalAdministeredItemSchema = z.object({
  type: z.literal('internal_administered'),
  ...internalFields,
  repeatsAllowed: z.literal(0).optional(),
  allocations: z.array(batchAllocationItemSchema).min(1),
  /** Per-disease booster reminders to create. Vaccine-only; optional. */
  reminders: z
    .array(z.object({ target: treatmentReminderTargetSchema, dueAt: z.iso.date() }))
    .optional(),
})

const externalFields = {
  ...sharedFields,
  catalogProductId: z.uuid(),
  instructions: z.string(),
  veterinaryCascade: z.boolean().optional(),
}

/** External prescription — written script, no stock impact. */
const createExternalPrescriptionItemSchema = z.object({
  type: z.literal('external_prescription'),
  ...externalFields,
})

/** External prescription with a task for a clinic team to print and sign. */
const createExternalPrescriptionWithTaskItemSchema = z.object({
  type: z.literal('external_prescription_with_task'),
  ...externalFields,
  clinicId: clinicIdSchema,
})

export const createTreatmentItemSchema = z.discriminatedUnion('type', [
  createInternalItemSchema,
  createInternalAdministeredItemSchema,
  createExternalPrescriptionItemSchema,
  createExternalPrescriptionWithTaskItemSchema,
])
export type CreateTreatmentItem = z.infer<typeof createTreatmentItemSchema>

/**
 * Flattens a CreateTreatmentItem discriminated union into a single object
 * keyed to treatment DB columns. Handles the union narrowing in one place
 * so callers don't need `'key' in item` guards.
 */
export function flattenTreatmentPayload(item: CreateTreatmentItem, treatmentType: TreatmentType) {
  if (
    (item.repeatsAllowed ?? 0) > 0 &&
    (treatmentType !== 'prescription' || item.type === 'internal_administered')
  ) {
    throw new ValidationError('Only take-home prescriptions can have refills')
  }
  return {
    productId: 'productId' in item ? item.productId : null,
    catalogProductId: 'catalogProductId' in item ? item.catalogProductId : null,
    quantity: item.quantity,
    instructions: 'instructions' in item ? (item.instructions ?? null) : null,
    repeatsAllowed: item.repeatsAllowed ?? null,
    refillInterval: item.type === 'internal' ? (item.refillInterval ?? null) : null,
    validUntil: item.validUntil ? new Date(item.validUntil) : null,
    veterinaryCascade: 'veterinaryCascade' in item ? (item.veterinaryCascade ?? false) : false,
    tagNumber:
      treatmentType === 'vaccination' && 'tagNumber' in item ? (item.tagNumber ?? null) : null,
    injectionSite:
      treatmentType === 'vaccination' && 'injectionSite' in item
        ? (item.injectionSite ?? null)
        : null,
    clinicalNotes: item.clinicalNotes ?? null,
    internalNotes: item.internalNotes ?? null,
  }
}

export type CreateInternalItem = z.infer<typeof createInternalItemSchema>
export type CreateInternalAdministeredItem = z.infer<typeof createInternalAdministeredItemSchema>
export type CreateExternalPrescriptionItem = z.infer<typeof createExternalPrescriptionItemSchema>
export type CreateExternalPrescriptionWithTaskItem = z.infer<
  typeof createExternalPrescriptionWithTaskItemSchema
>

export const postTreatmentsBodySchema = z.object({
  patientId: z.uuid(),
  /** Present when recording treatments within a consultation context. */
  consultationId: z.uuid().optional(),
  /** Treatment items to create. At least one is required. */
  items: z.array(createTreatmentItemSchema).min(1),
})
export type PostTreatmentsBody = z.infer<typeof postTreatmentsBodySchema>

// ---------------------------------------------------------------------------
// POST /api/treatments/:id/external-scripts — save an e-signed written prescription
// ---------------------------------------------------------------------------

export const postExternalScriptBodySchema = writtenPrescriptionInputSchema.omit({
  treatmentId: true,
})

// ---------------------------------------------------------------------------
// POST /api/treatments/:id/refill — create a new order for an existing treatment
// ---------------------------------------------------------------------------

export const postTreatmentRefillBodySchema = z.object({
  /** Clinic from which the refill order will be fulfilled. */
  clinicId: clinicIdSchema,
})
export type PostTreatmentRefillBody = z.infer<typeof postTreatmentRefillBodySchema>

// ---------------------------------------------------------------------------
// POST /api/treatments/:id/cancel — cancel a treatment
// ---------------------------------------------------------------------------

export const postTreatmentCancelBodySchema = z.object({
  reason: z.string().min(1, 'Cancellation reason is required'),
})
export type PostTreatmentCancelBody = z.infer<typeof postTreatmentCancelBodySchema>

// ---------------------------------------------------------------------------
// PUT /api/treatments/:id — update a treatment in-place
// ---------------------------------------------------------------------------

/**
 * Mirrors the create union but the administered variant carries reminder IDs
 * for reconciliation (existing reminders include their server ID so the
 * backend can diff against the current state).
 *
 * Allocations don't carry IDs today - they're rebuilt wholesale when changed -
 * but the same pattern could be extended there if allocation editing becomes more granular.
 */
const putInternalAdministeredItemSchema = z.object({
  ...createInternalAdministeredItemSchema.shape,
  reminders: z
    .array(
      z.object({
        id: z.uuid().optional(),
        target: treatmentReminderTargetSchema,
        dueAt: z.iso.date(),
      })
    )
    .optional(),
})

export const putTreatmentBodySchema = z.discriminatedUnion('type', [
  createInternalItemSchema,
  putInternalAdministeredItemSchema,
  createExternalPrescriptionItemSchema,
  createExternalPrescriptionWithTaskItemSchema,
])
export type PutTreatmentBody = z.infer<typeof putTreatmentBodySchema>
